import React, { useContext, useState } from 'react';
import { SiteContext } from '../../../context/SiteContext';
import TextInput from '../../common/TextInput/TextInput';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import './NeedHelpPage.scss';
import { ButtonBack } from '../../common/Button/ButtonBack';
import { useNavigate, useOutletContext } from 'react-router-dom';
import Links from '../../common/Widgets/Links';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons';
import DateSelector from '../../common/DateSelector/DateSelector';
import { getInsuranceJourneyMinMaxDob } from '../../common/HelperMethods';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import {
  InsuranceJourneyContext,
  InsuranceJourneyState
} from '../../common/CommonTypes';

export type SecurityErrors = {
  dateOfBirth: string;
  postcode: string;
};

const initialErrors = {
  dateOfBirth: '',
  postcode: ''
};

const LotusSecurityPage = (): JSX.Element => {
  const [updateDobError, setUpdateDobError] = useState(false);
  const [dobIsValid, setDobIsValid] = useState(true);
  const [dateOfBirth, setDateOfBirth] = useState<Date>();
  const [postcode, setPostcode] = useState<string>('');
  const [errors, setErrors] = useState<SecurityErrors>(initialErrors);
  const navigate = useNavigate();
  const { pageEvents } = useOutletContext<InsuranceJourneyContext>();
  const { commonEvents } = pageEvents;
  const { setQuote } = commonEvents;

  const dobRequiredMsg = 'Date of birth is required';
  const dobInvalidMsg = 'Invalid date of birth';
  const ageOutOfRange = 'Age must be between 17 and 90';

  const { site } = useContext(SiteContext);

  const { minDob, maxDob } = getInsuranceJourneyMinMaxDob();

  const today = new Date();

  const isAgeValid = (): boolean => {
    if (dateOfBirth === undefined) {
      return false;
    }

    let age = today.getFullYear() - dateOfBirth.getFullYear();
    const monthDiff = today.getMonth() - dateOfBirth.getMonth();
    if (
      monthDiff < 0 ||
      (monthDiff === 0 && today.getDate() < dateOfBirth.getDate())
    ) {
      age--;
    }

    const result = age >= 17 && age <= 90;
    return result;
  };

  const validateDetails = (): boolean => {
    let isValid = true;

    const error: SecurityErrors = {
      ...initialErrors
    };

    if (dobIsValid === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobInvalidMsg;
    } else if (CommonValidation.isEmpty(dateOfBirth)) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobRequiredMsg;
    } else if (isAgeValid() === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = ageOutOfRange;
    }

    if (CommonValidation.isEmpty(postcode)) {
      error.postcode = 'Postcode is required';
      isValid = false;
    } else if (!CommonValidation.isValidPostcode(postcode)) {
      error.postcode = 'Postcode is invalid';
      isValid = false;
    }

    setErrors(error);

    return isValid;
  };

  const getCustomerDetails = async (
    dob: Date | undefined,
    postcode: string
  ) => {
    const controller = new AbortController();

    const onSuccess = (insuranceState: InsuranceJourneyState): void => {
      console.log(insuranceState);
      setQuote(insuranceState);
    };

    const onFailure = (error: string): void => {
      console.log(error);
    };

    await new CarInsuranceApi().GetMobiliseQuoteAsync(
      site.user.token,
      site.brand.tiaProductCode,
      postcode,
      dob?.toISOString() ?? '',
      site.vPol,
      onSuccess,
      onFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const goBack = (): void => {
    navigate(-1);
  };

  const onClickContinue = async () => {
    const isValid = validateDetails();
    if (isValid) {
      await getCustomerDetails(dateOfBirth, postcode);
    }
  };

  const chevronLeftComponent = <FontAwesomeIcon icon={faChevronLeft} />;

  const onDobValidated = (isValid: boolean): void => {
    setUpdateDobError(false);
    setDobIsValid(isValid);
  };

  const onChangeDateOfBirth = (dob: Date | undefined) => {
    if (dob) {
      setDateOfBirth(dob);
    }
  };

  const onChangePostcode = (postcode: string) => {
    setPostcode(postcode);
  };

  return (
    <div>
      <Links
        id='back-link'
        title=''
        items={[{ to: '', text: 'Back', onClick: goBack }]}
        icon={chevronLeftComponent}
      />
      <h2>For security, please complete the following fields</h2>
      <div className='table-flex-layout'>
        <div className='form-container'>
          <div className='one-col'>
            <div className='control-label'>Date of birth</div>
            <div className='control'>
              <DateSelector
                value={dateOfBirth}
                minDate={minDob}
                maxDate={maxDob}
                invalidDateMessage={errors.dateOfBirth}
                updateDobError={updateDobError}
                onChange={(value) => onChangeDateOfBirth(value)}
                onValidated={(isValid: boolean) => onDobValidated(isValid)}
                name='date-of-birth'
                culture={site.cultureCode}
              />
            </div>
          </div>
          <div className='one-col'>
            <div className='control-label'>Postcode</div>
            <div className='control'>
              <TextInput
                name='postcode'
                className='input-text'
                error={errors.postcode}
                value={postcode}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onChangePostcode(e.currentTarget.value)
                }
              />
            </div>
          </div>
        </div>
        <div className='flex-row form-nav'>
          <ButtonBack
            name='btnBack'
            text='Back'
            onClick={goBack}
            additionalClassNames='btn-back'
          />
          <ButtonContinue
            name='btnContinuee'
            text='Continue'
            onClick={() => {
              onClickContinue();
            }}
            additionalClassNames='btn-continue'
          />
        </div>
      </div>
    </div>
  );
};

export default LotusSecurityPage;
